/**
 * The PR watch and Merge (decision 0059, wave D). The runner watches the open PRs the hub lists for it with the
 * person's own `gh`, reports material changes (`ReportPrs` → `RecordPrState`), and executes signed merge orders.
 * Every string GitHub supplies is cut to its cap by the runner before sending; the schemas reject anything longer.
 */
import { Schema } from "effect"
import {
  ArtifactId,
  MergeId,
  Millis,
  NonNegInt,
  PosInt,
  ProjectId,
  ProposalId,
  RunnerId,
  Sha256,
  UserId,
  WorkspaceId,
} from "./ids"

// #region github
/** github.com only this wave. `owner/name` as GitHub spells it (case kept; compared case-insensitively). */
export const GithubRepo = Schema.String.check(
  Schema.isPattern(/^[A-Za-z0-9](?:[A-Za-z0-9-]{0,38})\/[A-Za-z0-9._-]{1,100}$/),
)
export const GithubLogin = Schema.String.check(
  Schema.isPattern(/^[A-Za-z0-9](?:[A-Za-z0-9-]{0,38})(?:\[bot\])?$/),
)
export type GithubLogin = typeof GithubLogin.Type
export const GitSha = Schema.String.check(Schema.isPattern(/^[0-9a-f]{40}(?:[0-9a-f]{24})?$/))
export type GitSha = typeof GitSha.Type
/** A short excerpt of a review or comment (outside text: shown quoted, marks its section tainted). */
export const PrExcerpt = Schema.String.check(Schema.isMaxLength(280))

const GITHUB_OWNER = /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,38})$/
const GITHUB_NAME = /^[A-Za-z0-9._-]{1,100}$/

/**
 * `https://github.com/<owner>/<repo>/pull/<n>[/…][?…][#…]` rebuilt from its parts, or null (moved here from the web's
 * `githubPull`). The URL is agent-reported, so it is tainted: no other scheme or host (a look-alike host such as
 * `github.com.evil.example` never passes), no credentials, no port, no `.`/`..` segments, no backslashes or
 * whitespace inside. Parsed by hand (the protocol has no `URL` in its lib), stricter than WHATWG on purpose.
 */
export const parseGithubPull = (
  raw: string,
): { readonly url: string; readonly repo: string; readonly number: number } | null => {
  const s = raw.trim()
  if (s.length === 0 || s.length > 2048) return null
  // biome-ignore lint/suspicious/noControlCharactersInRegex: control characters are what this rejects
  if (/[\s\u0000-\u001f\u007f\\]/.test(s)) return null
  const m = /^https:\/\/([^/?#]*)(\/[^?#]*)?(?:[?#].*)?$/i.exec(s)
  if (!m) return null
  const authority = m[1] ?? ""
  if (authority.includes("@") || authority.includes(":")) return null
  if (authority.toLowerCase() !== "github.com") return null
  const segments = (m[2] ?? "").split("/").filter((x) => x.length > 0)
  if (segments.some((x) => x === "." || x === "..")) return null
  const [owner, repo, pull, n] = segments
  if (!owner || !repo || pull !== "pull" || !n || !/^[1-9]\d{0,9}$/.test(n)) return null
  if (!GITHUB_OWNER.test(owner) || !GITHUB_NAME.test(repo)) return null
  return { url: `https://github.com/${owner}/${repo}/pull/${n}`, repo: `${owner}/${repo}`, number: Number(n) }
}

export const PrState = Schema.Literals(["open", "merged", "closed"])
export type PrState = typeof PrState.Type
export const PrMergeable = Schema.Literals(["mergeable", "conflicting", "unknown"])
export const PrReviewDecision = Schema.NullOr(
  Schema.Literals(["approved", "changes_requested", "review_required"]),
)
export const PrChecksState = Schema.NullOr(
  Schema.Literals(["pending", "success", "failure", "error", "expected"]),
)
export const MergeMethod = Schema.Literals(["squash", "merge", "rebase"])
export type MergeMethod = typeof MergeMethod.Type
/** GraphQL `MergeStateStatus`, lower-cased. */
export const PrMergeState = Schema.Literals([
  "behind",
  "blocked",
  "clean",
  "dirty",
  "draft",
  "has_hooks",
  "unknown",
  "unstable",
])

const PrUrl = Schema.String.check(Schema.isMaxLength(300))

/**
 * `excerpt` exists **on the wire only** (runner → hub → command). Core strips it before it stores a snapshot and keeps
 * it once, on the fact that introduces the review or comment. Absent when the runner has `prWatchExcerpts = false`.
 */
export const PrReview = Schema.Struct({
  login: GithubLogin,
  state: Schema.Literals(["approved", "changes_requested", "commented", "dismissed"]),
  /** authorAssociation OWNER | MEMBER | COLLABORATOR: someone who can write to the repo. */
  writer: Schema.Boolean,
  bot: Schema.Boolean,
  at: Millis,
  url: PrUrl,
  commitSha: Schema.NullOr(GitSha),
  excerpt: Schema.optionalKey(PrExcerpt),
})
export type PrReview = typeof PrReview.Type
export const PrComment = Schema.Struct({
  login: GithubLogin,
  writer: Schema.Boolean,
  bot: Schema.Boolean,
  at: Millis,
  url: PrUrl,
  excerpt: Schema.optionalKey(PrExcerpt),
})
export type PrComment = typeof PrComment.Type

/**
 * What one watch query says about one PR, normalized by the runner (or by the hub's GitHub App path, decision 0069).
 * The hub stamps `observedAt`/`observedBy` (a runner id, or `github_app` for a webhook or reconcile)/`ownRepo`.
 */
export const PrSnapshot = Schema.Struct({
  artifactId: ArtifactId,
  /** = parseGithubPull(artifact.uri).url */
  url: Schema.String,
  repo: GithubRepo,
  number: PosInt,
  state: PrState,
  draft: Schema.Boolean,
  headSha: GitSha,
  headRef: Schema.String.check(Schema.isMaxLength(255)),
  baseRef: Schema.String.check(Schema.isMaxLength(255)),
  mergeable: PrMergeable,
  /** mergeStateStatus: classifies refusals. */
  mergeState: PrMergeState,
  reviewDecision: PrReviewDecision,
  /** latestReviews: the newest per person. */
  reviews: Schema.Array(PrReview).check(Schema.isMaxLength(10)),
  comments: Schema.Struct({ total: NonNegInt, latest: Schema.Array(PrComment).check(Schema.isMaxLength(5)) }),
  checks: Schema.Struct({
    state: PrChecksState,
    failing: Schema.Array(Schema.String.check(Schema.isMaxLength(100))).check(Schema.isMaxLength(10)),
    running: NonNegInt,
  }),
  /** The head commit's first author with a GitHub user. */
  headAuthor: Schema.NullOr(GithubLogin),
  mergedBy: Schema.NullOr(GithubLogin),
  mergedAt: Schema.NullOr(Millis),
  mergeSha: Schema.NullOr(GitSha),
  closedAt: Schema.NullOr(Millis),
  /** off | armed (autoMergeRequest set: merges when checks pass) | in_queue (isInMergeQueue). */
  autoMerge: Schema.Literals(["off", "armed", "in_queue"]),
  /** The method Merge would use in this repo; null = none of the three is allowed. */
  mergeMethod: Schema.NullOr(MergeMethod),
  /** The reporting runner's gh login (`viewer.login`). Tells a run's merge or comment from the person's. */
  viewerLogin: Schema.NullOr(GithubLogin),
  updatedAt: Millis,
  /** Hub receive time (the runner's value is overwritten). */
  observedAt: Millis,
  /**
   * The reporting runner, or `github_app` when the hub re-read the PR after a GitHub App webhook or reconcile
   * (decision 0069; overwritten by the hub). Not in the material hash. A widening: older snapshots decode.
   */
  observedBy: Schema.Union([RunnerId, Schema.Literal("github_app")]),
  /**
   * Stamped by the hub (the runner's value is overwritten): `repo` is the workspace Repo of the watch entry's `repoId`
   * (`Repo.remote`, case-insensitive). Core offers Merge only when true.
   */
  ownRepo: Schema.Boolean,
})
export type PrSnapshot = typeof PrSnapshot.Type

/**
 * Hub → runner: one PR to watch. `lastHash` = the hash the **hub** holds (`hub_pr_watch.last_hash`), so a restarted
 * runner re-sends nothing new. `hot` = poll every 60 s; otherwise every 10 min.
 */
export const WatchedPr = Schema.Struct({
  artifactId: ArtifactId,
  projectId: ProjectId,
  url: Schema.String,
  repo: GithubRepo,
  number: PosInt,
  hot: Schema.Boolean,
  lastHash: Schema.NullOr(Sha256),
})
export type WatchedPr = typeof WatchedPr.Type

/** Why GitHub (or the runner) refused a merge. The sentence is built by core. */
export const MergeRefusal = Schema.Literals([
  "sha_moved",
  "checks_failing",
  "checks_pending",
  "conflicts",
  "behind",
  "review_required",
  "protected",
  "closed",
  "draft",
  "gh_logged_out",
  "gh_missing",
  "no_access",
  "no_method",
  "too_late",
  "other",
])
export type MergeRefusal = typeof MergeRefusal.Type
export const MergeResult = Schema.TaggedUnion({
  /**
   * `byOrder` = this runner's own `gh pr merge` exited 0 and the re-query shows MERGED with `mergeSha`.
   * false = the PR was already merged (by someone else, or a retry) when the order ran.
   */
  merged: { sha: Schema.NullOr(GitSha), byOrder: Schema.Boolean },
  /** merge queue / auto-merge armed; the watch sees the merge later */
  queued: {},
  refused: { reason: MergeRefusal, detail: Schema.NullOr(Schema.String.check(Schema.isMaxLength(200))) },
  /** The hub: no runner reported before `notAfter + MERGE_ORDER_GRACE_MS`. `sent` = a runner had received it. */
  expired: { sent: Schema.Boolean },
})
export type MergeResult = typeof MergeResult.Type
export const MergeReport = Schema.Struct({ mergeId: MergeId, artifactId: ArtifactId, result: MergeResult })
export type MergeReport = typeof MergeReport.Type

/** The order the hub signs (Ed25519, the job signer) and pushes to one runner. `aud` keeps it apart from a JobSpec. */
export const MergeOrder = Schema.Struct({
  v: Schema.Literal(1),
  aud: Schema.Literal("merge"),
  mergeId: MergeId,
  workspaceId: WorkspaceId,
  projectId: ProjectId,
  runnerId: RunnerId,
  artifactId: ArtifactId,
  url: Schema.String,
  repo: GithubRepo,
  number: PosInt,
  headSha: GitSha,
  notAfter: Millis,
})
export type MergeOrder = typeof MergeOrder.Type
/** payload = canonicalJson(encode(toCodecJson(MergeOrder))(order)); sig = base64url Ed25519 over its UTF-8 bytes (as SignedJob). */
export const SignedMergeOrder = Schema.Struct({
  kid: Schema.String,
  payload: Schema.String,
  sig: Schema.String,
})

/** What the watcher shows on the machine (console manifest `prWatch`, `minga status`). Display only. */
export const PrWatchNotice = Schema.Struct({
  state: Schema.Literals([
    "idle",
    "watching",
    "backing_off",
    "rate_limited",
    "gh_missing",
    "gh_logged_out",
    "disabled",
  ]),
  watching: NonNegInt,
  repos: NonNegInt,
  lastCheckAt: Schema.NullOr(Millis),
  nextCheckAt: Schema.NullOr(Millis),
  detail: Schema.NullOr(Schema.String.check(Schema.isMaxLength(200))),
  /**
   * The runner's gh login (`viewer.login`), absent when unknown (decision 0069): the hub's GitHub App path uses it as
   * the snapshot's `viewerLogin`, since the app's own GraphQL viewer is the bot. Older clouds ignore it.
   */
  login: Schema.optionalKey(GithubLogin),
})
export type PrWatchNotice = typeof PrWatchNotice.Type

/** How a merge came about: a person on GitHub, a person's Merge in Minga, or a Minga run with the person's gh. */
export const MergeVia = Schema.Literals(["github", "minga", "run"])
export type MergeVia = typeof MergeVia.Type

/**
 * What changed on a PR, as core reads it from two snapshots. `key` is the dedupe key: a fact whose key the artifact was
 * already told is dropped, whichever runner saw it and however often.
 */
export const PrFact = Schema.TaggedUnion({
  merged: {
    key: Schema.String,
    login: Schema.NullOr(GithubLogin),
    sha: Schema.NullOr(GitSha),
    at: Schema.NullOr(Millis),
    via: MergeVia,
  },
  closed: { key: Schema.String, at: Schema.NullOr(Millis) },
  changes_requested: {
    key: Schema.String,
    login: GithubLogin,
    writer: Schema.Boolean,
    url: PrUrl,
    excerpt: Schema.optionalKey(PrExcerpt),
  },
  /** `fromRun`: every new comment is by the runner's gh login while a run of the item was live (non-waking). */
  commented: {
    key: Schema.String,
    count: NonNegInt,
    logins: Schema.Array(GithubLogin),
    url: PrUrl,
    excerpt: Schema.optionalKey(PrExcerpt),
    fromRun: Schema.Boolean,
  },
  ci_failed: { key: Schema.String, sha: GitSha, failing: Schema.Array(Schema.String) },
  conflicts: { key: Schema.String, base: Schema.String },
  new_commits: { key: Schema.String, sha: GitSha, login: Schema.NullOr(GithubLogin) },
  approved: { key: Schema.String, login: Schema.NullOr(GithubLogin) },
  ci_passed: { key: Schema.String, sha: GitSha },
})
export type PrFact = typeof PrFact.Type

/** Where the reviewed SHA of a human review card came from. */
export const PrReviewSource = Schema.Literals(["run_head", "evidence", "snapshot"])

/** The artifact's PR view (written by evolve only): the stored snapshot (no excerpts), its hash, the told fact keys. */
export const PrView = Schema.Struct({
  snapshot: PrSnapshot,
  hash: Sha256,
  told: Schema.Array(Schema.String).check(Schema.isMaxLength(50)),
  /** How the PR was merged (the `merged` fact's `via`), once it is; read by core's `runMergeOf`. */
  mergedVia: Schema.optionalKey(MergeVia),
  /** The last `PrReviewBound`: the SHA the open human card was opened for. */
  reviewed: Schema.optionalKey(
    Schema.Struct({ proposalId: ProposalId, sha: GitSha, source: PrReviewSource }),
  ),
})
export type PrView = typeof PrView.Type

/** A merge chosen in Minga (written by evolve only). An immediate refusal sets it with `state: "refused"`. */
export const MergeView = Schema.Struct({
  mergeId: MergeId,
  proposalId: ProposalId,
  headSha: GitSha,
  by: UserId,
  at: Millis,
  notAfter: Millis,
  state: Schema.Literals(["merging", "queued", "merged", "refused", "expired"]),
  sha: Schema.NullOr(GitSha),
  sentence: Schema.NullOr(Schema.String),
  endedAt: Schema.NullOr(Millis),
})
export type MergeView = typeof MergeView.Type

export const PR_WATCH_EVERY_MS = 60_000 // hot PRs
export const PR_WATCH_COLD_EVERY_MS = 10 * 60_000 // the rest
export const PR_WATCH_QUIET_DROP_MS = 30 * 86_400_000 // a cold PR unchanged this long leaves the list
export const PR_WATCH_MAX_PER_QUERY = 20 // PRs per call, across repos
export const MERGE_ORDER_TTL_MS = 15 * 60_000
export const MERGE_ORDER_MIN_LEFT_MS = 4 * 60_000 // a runner starts a merge only with this much time left
export const MERGE_ORDER_GRACE_MS = 5 * 60_000 // the hub expires at notAfter + this (> the runner's worst case)
export const MERGE_RUN_WINDOW_MS = 60_000 // ± around a run window for "a run merged"
// #endregion github
